import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { RefreshCw02Icon } from '@/components/icons/RefreshCw02Icon';
import {
  AssetIntegrationProviders,
  getIntegrationProviderDisplayName,
} from '@/modules/assetProviderIntegrations/shared/constants';
import { AssetIntegration_EntityFragment } from '@/modules/assetProviderIntegrations/shared/graphql/assetProviderIntegration.fragments.generated';
import {
  entityHasAssetsIntegration,
  getEntityAssetsIntegrationProvider,
  getIntegrationEntitiesFromEntity,
} from '@/modules/assetProviderIntegrations/shared/utils';
import {
  EntityValuationAction,
  EntityValuationActionDefinition,
} from '@/modules/entities/details/EntityValuation/EntityValuationActions';

/**
 * Gets the available integration actions for an entity
 * @param entity Entity with potential integration
 * @param isViewOnly Whether the user is in view-only mode
 * @returns Array of integration actions
 */
export function getEstateIntegrationActions(
  entity: AssetIntegration_EntityFragment,
  isViewOnly: boolean = false
): EntityValuationActionDefinition[] {
  const integrationEntities = getIntegrationEntitiesFromEntity(entity);

  if (!entityHasAssetsIntegration(entity) || !integrationEntities) {
    return [];
  }

  const provider = getEntityAssetsIntegrationProvider(entity);
  if (!provider) {
    return []; // TypeScript guard
  }

  const providerName = getIntegrationProviderDisplayName(provider);
  const actions: EntityValuationActionDefinition[] = [];

  // Add resync action for non-view-only users
  if (!isViewOnly) {
    actions.push({
      kind: EntityValuationAction.RESYNC_ENTITY_VALUATION,
      label: 'Resync valuation data',
      icon: RefreshCw02Icon,
    } as const);
  }

  // Add view on platform actions
  if (provider === AssetIntegrationProviders.EMONEY) {
    const eMoneyEntity = integrationEntities[0];
    if (eMoneyEntity) {
      actions.push({
        kind: EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM,
        name: 'eMoney Client Overview',
        integrationEntityId: eMoneyEntity.id,
        icon: LinkExternal01Icon,
      } as const);
    }
  } else {
    actions.push(
      ...integrationEntities.map(
        (ie) =>
          ({
            kind: EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM,
            name: `Open "${ie.name}" in ${providerName}`,
            integrationEntityId: ie.id,
            icon: LinkExternal01Icon,
          }) as const
      )
    );
  }

  // Add manage data link action
  actions.push({
    kind: EntityValuationAction.MANAGE_DATA_LINK,
    label: `Manage ${providerName || 'integration'} data link`,
    icon: Edit02Icon,
  } as const);

  return actions;
}
